import http from './http'
import Vue from 'vue'
import eventBus from './event-bus'

/**
 * A simple reactive store inspired by this article: https://austincooper.dev/2019/08/09/vue-observable-state-store/
 */
const store = Vue.observable({ // this is the magic
	filters: [],
	sortBy: 'rankingDesc',
	pagination: {
		per_page: 15,
		total: 0,
		from: undefined,
		to: undefined,
		page: 1,
		last_page: undefined
	},
	utm: [],
	isBusinessCustomer: false,
	dialogSearchText: '',
	sortByItems: [
		{
			label: 'Beliebtheit',
			value: 'rankingDesc'
		},
		{
			label: 'Preis (aufsteigend)',
			value: 'priceAsc'
		},
		{
			label: 'Preis (absteigend)',
			value: 'priceDesc'
		},
		{
			label: 'Neuste zuerst',
			value: 'activeSince'
		}
	]
})


store.actions = {
	/**
	 * set the selectedValues of each filter from the passed params
	 * @param params {URLSearchParams} - the filter to get the value from
	 */
	async setFiltersFromURLSearchParams(params) {
		const filters = []
		for (let key of params.keys()) {
			const filter = store.filters.find(f => f.name === key)
			let selectedValues = []
			let query_value = http.parseQueryParam(params.get(key))
			if(!filter) {
				continue;
			}
			if(filter.type === 'duration') {
				if (!Array.isArray(query_value)) {
					query_value = [-1, -1];
				}
				let durationValue = query_value[0] ?? -1;
				let sliderValue = query_value[1] ?? -1;

				durationValue = filter.duration_options.find(v => v.value === durationValue)
				sliderValue = filter.values.find(v => v.value === sliderValue)

				if(!durationValue) {
					durationValue = filter.duration_options.find(v => v.default)
				}
				if(!sliderValue) {
					sliderValue = filter.values.find(v => v.default)
				}
				filter.selectedValues = [durationValue, sliderValue];

				filters.push(filter);

			} else {
				if (filter.values && filter.values.length) {
					const values = Array.isArray(query_value) ? query_value : [query_value]
					for (let value of values) {
						const valueObj = filter.values.find(v => v.value === value)
						if (!valueObj) {
							if (filter.values.length < 2 && value >= 0) selectedValues = [values] // parse the value only on filters with no values or only one default value
							else if (filter.values.includes(value)) selectedValues = [values]
						} else if (!valueObj.default) selectedValues.push(valueObj)
					}
				} else selectedValues.push(query_value)
				if (selectedValues.length) {
					filter.selectedValues = selectedValues
					filters.push(filter)
				}
			}
		}
		await store.actions.updateFiltersInStore(filters)
	},

	/**
	 * return true if an dependency filter should be disabled
	 * @param filter - filter to get the status for
	 * @param filters - the list of all filters, defaults to store.filters
	 * @returns {boolean|boolean|*}
	 */
	getDisabledStatus(filter, filters = store.filters) {
		if (filter.type !== 'dependency') return false

		const parentFilter = filters.find(f => f.name === filter.depends.on)
		if (!parentFilter) {
			console.warn('dependency filters depend on is not in the filters list')
			return true
		}

		if (parentFilter.selectedValues && parentFilter.selectedValues.length) {
			return (parentFilter.selectedValues.length === 1 && parentFilter.selectedValues[0].default)
		} else return true
	},


	/**
	 * returns the selected value or the default value of the passed filter
	 * @param filter {Object} - the filter to get the value from
	 * @returns {any} - the selected value or default value
	 */
	getSelectedValueOrDefaultFromFilter(filter) {
		let value = ''

		if(filter.type === 'duration') {
			let sliderValue = '';
			if(!filter.selectedValues && !Array.isArray(filter.selectedValues)) {
				return [-1, -1]
			}
			let durationValue = filter.selectedValues[0].value ?? -1;
			if(filter.selectedValues[1] === undefined) {
				sliderValue = -1
			} else {
				sliderValue = filter.selectedValues[1].value ?? -1;
			}

			durationValue = filter.duration_options.find(v => v.value === durationValue);
			sliderValue = filter.values.find(v => v.value === sliderValue)

			if(durationValue === null || durationValue === undefined) {
				durationValue = filter.duration_options.find(f => {
					return f.default
				});
			}

			if(sliderValue === null || sliderValue === undefined) {
				sliderValue = filter.values.find(f => {
					return f.default
				});
			}

			return [
				durationValue.value, sliderValue.value
			]
		}
		if (filter.selectedValues && filter.selectedValues.length) {
			value = []

			for (let val of filter.selectedValues) {

				if (!val) {
					console.warn('no value defined on a selected value:', filter)
					continue
				}
				// check if val is an Object and has the key 'value'
				if (Object.prototype.hasOwnProperty.call(val, 'value')) value.push(val.value)
				else value.push(val)
			}

		} else if (filter.values && filter.values.length) {
			const defaultFilter = filter.values.find(f => {
				return f.default
			})

			if (defaultFilter) value = defaultFilter.value
			else console.warn('no default filter for:', filter)
		}

		return value
	}
	,

	/**
	 * returns the request params for vehicle filter requests
	 * @param overrideFilters {Array|Object} - optional filters which overwrites the generated request params
	 * @returns {Object}
	 */
	getRequestParameter(overrideFilters = undefined) {
		const params = {}

		function setParam(filter) {
			params[filter.name] = store.actions.getSelectedValueOrDefaultFromFilter(filter)
		}

		for (let filter of store.filters) {
			setParam(filter)
		}

		params.per_page = store.pagination.per_page
		params.page = store.pagination.page
		params.sort = store.sortBy
		store.utm.forEach((val) => {
			params[val.key] = val.value;
		})
		params.is_business = store.isBusinessCustomer;

		if (overrideFilters) {
			if (!Array.isArray(overrideFilters)) overrideFilters = [overrideFilters]

			for (let filter of overrideFilters) {
				setParam(filter)
			}
		}
		return params
	},

	async updateFiltersInStore(filterOrFilters) {
		if (!Array.isArray(filterOrFilters)) filterOrFilters = [filterOrFilters]
		if (window.datalayer) {
			window.dataLayer.push({ filter: null });
		}

		let dependency_filters = store.filters.filter(f => f.type === 'dependency')

		for (let filter of filterOrFilters) {
			const filterFromStore = store.filters.find(f => {
				return f.name === filter.name
			})
			Object.assign(filterFromStore, filter)

		}

		const asyncFilter = dependency_filters.map(async (dependency_filter) => {
			const filter = store.filters.filter(f => f.name === dependency_filter.depends.on);
			const default_value = dependency_filter.values.find(v => v.default)

			if (!filter || !Array.isArray(filter[0].selectedValues)) {
				dependency_filter.values = [default_value]
				dependency_filter.selectedValues = [default_value]
				return dependency_filter;
			}
			const parent_ids = []
			let filter_values = [default_value]
			filter[0].selectedValues.forEach(value => {
				parent_ids.push(value.id || value.value || value)
			})
			for (let id of parent_ids) {
				if(default_value.value === id) {
					continue;
				}
				let url = dependency_filter.depends.valuesUrl.replace(`{${dependency_filter.depends.on}}`, id)
				let value = await http.get(url)
				if (value) {
					if (Array.isArray(value)) {
						filter_values = [...filter_values, ...value]
					} else {
						filter_values.push(value)
					}
				}
			}
			const mappedValues = filter_values.filter((item) => {
				return item.visible === 1 || item.default === true || item.visible === null
			}).map(item => {
				return {
					label: item.label || item.title,
					value: item.id,
					id: item.id,
					default: item.default || false
				}
			});
			if(!Array.isArray(dependency_filter.selectedValues)) {
				return dependency_filter;
			}
			let selectedValues = dependency_filter.selectedValues;

			if(Array.isArray(dependency_filter.selectedValues[0])) {
				selectedValues = dependency_filter.selectedValues[0].map((e) => {
					return mappedValues.find(val => val.id === e);
				});
			}

			dependency_filter.selectedValues = selectedValues;
			dependency_filter.values = mappedValues;
			return dependency_filter;
		})
		Promise.all(asyncFilter).then(() => {
			eventBus.$emit('filters-updated')
		})

		if (window.dataLayer) {
			window.dataLayer.push({
				'filter': {
					'label': filterOrFilters[0].label,
					'value': filterOrFilters[0].selectedValues
				}
			});
		}

	},

	updatePagination(meta) {
		const pagination = {
			// per_page: meta.per_page,
			total: parseInt(meta.total),
			to: parseInt(meta.to),
			from: parseInt(meta.from),
			page: parseInt(meta.current_page || meta.page),
			last_page: parseInt(meta.last_page)
		}

		Object.assign(store.pagination, pagination)
	},

	async getFilters() {
		if (!store.filters || !store.filters.length) store.filters = await http.get('filters/templates')
		return store.filters
	},

	async resetAllFilters() {
		for (let filter of store.filters) {
			if (filter.selectedValues) {
				filter.selectedValues = undefined
				await store.actions.updateFiltersInStore(filter)
			}
		}
	},

	/**
	 * returns the vehicles of a request, using the current filters
	 * @param overrideFilters {Array} - optional filters which overwrites the generated request params
	 * @returns {Object}
	 */
	async getVehicles(overrideFilters = undefined) {
		if (!store.filters.length) return

		const params_str = http.convertParamsToUrlQuery(store.actions.getRequestParameter(overrideFilters))

		const response = await http.get('vehicles' + params_str, false)

		window.history.pushState({}, '', params_str)

		store.actions.updatePagination(response.meta)

		return response.data
	},

	/**
	 * returns the total count of vehicles of a request, using the current filters
	 * @param overrideFilters {Array} - optional filters which overwrites the generated request params
	 * @returns {Number}
	 */
	async getVehiclesCount(overrideFilters) {
		if (!store.filters.length) return

		let params = store.actions.getRequestParameter(overrideFilters)
		params.onlyCount = true
		const params_str = http.convertParamsToUrlQuery(params)

		const response = await http.get('vehicles' + params_str, true)

		return response.meta.total
	}
}

/*
const getters = {
	getfilter: () => state.filter
}

const mutations = {
	setFilter: (prop, val) => state.filter[prop] = val
}*/

Vue.prototype.$store = store

//export { state, getters, mutations }

export default store
